import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  getAuditLogs, 
  getAuditLogMetadata, 
  downloadAuditLogs
} from '../services/auditLogService';
import { Permissions } from '../constants/permissions';
import useAuthStore from '../store/authStore';
import { AuditLogsManagementTable } from './AuditLogsManagementTable';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const AuditLogPanel = ({ showFilters = false, onShowFiltersChange }) => {
  const { user, hasPermission } = useAuthStore();
  const [logs, setLogs] = useState([]);
  const [metadata, setMetadata] = useState({ modules: [], actionTypes: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Filter state
  const [filters, setFilters] = useState({
    module: '',
    userEmail: '',
    startDate: '',
    endDate: '',
    success: '',
    search: '',
    limit: 10
  });

  // Check if user has permission to view audit logs
  const canViewAuditLogs = hasPermission(Permissions.AUDIT_LOG_READ);
  const canExportAuditLogs = hasPermission(Permissions.AUDIT_LOG_EXPORT);

  // Load initial data
  useEffect(() => {
    if (!canViewAuditLogs) return;
    
    const loadInitialData = async () => {
      try {
        const [metadataResponse] = await Promise.all([
          getAuditLogMetadata()
        ]);
        setMetadata(metadataResponse.data);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    
    loadInitialData();
  }, [canViewAuditLogs]);

  // Load audit logs
  const loadAuditLogs = useCallback(async (page = 1, currentFilters = filters) => {
    if (!canViewAuditLogs) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page,
        limit: pageSize,
        ...currentFilters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await getAuditLogs(params);
      setLogs(response.data);
      setCurrentPage(response.pagination.currentPage);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pageSize, canViewAuditLogs]);


  // Initial load and reload when filters change
  useEffect(() => {
    loadAuditLogs(1);
  }, [loadAuditLogs]);


  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    // Trigger immediate reload with new filters
    loadAuditLogs(1, newFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      module: '',
      userEmail: '',
      startDate: '',
      endDate: '',
      success: '',
      search: '',
      limit: 10
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadAuditLogs(1, clearedFilters);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    loadAuditLogs(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setFilters(prev => ({ ...prev, limit: newPageSize }));
    setCurrentPage(1);
    loadAuditLogs(1);
  };

  // Handle export
  const handleExport = async () => {
    if (!canExportAuditLogs) return;
    
    setExporting(true);
    try {
      const exportParams = { ...filters };
      // Remove empty filters
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
          delete exportParams[key];
        }
      });
      
      await downloadAuditLogs(exportParams);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };


  if (!canViewAuditLogs) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L3.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">You don't have permission to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <AdminPageLayout>
      {/* Filters */}
      {showFilters && (
        <AdminCard
          title="Filter Options"
          actions={
            Object.keys(filters).length > 0 ? (
              <button
                onClick={clearFilters}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                type="button"
              >
                Clear all filters
              </button>
            ) : null
          }
          bodyClassName="p-0"
        >
          <div className="admin-filter-panel">
            <div className="admin-filter-grid">
              {/* Module Filter */}
              <div>
                <label className="admin-filter-label">Module</label>
                <select
                  value={filters.module}
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  className="admin-filter-control"
                >
                  <option value="">All Modules</option>
                  {metadata.modules.map(module => (
                    <option key={module} value={module}>
                      {module.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Email Filter */}
              <div>
                <label className="admin-filter-label">User Email</label>
                <input
                  type="email"
                  value={filters.userEmail}
                  onChange={(e) => handleFilterChange('userEmail', e.target.value)}
                  placeholder="Filter by user email"
                  className="admin-filter-control"
                />
              </div>

              {/* Start Date Filter */}
              <div>
                <label className="admin-filter-label">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="admin-filter-control"
                />
              </div>

              {/* End Date Filter */}
              <div>
                <label className="admin-filter-label">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="admin-filter-control"
                />
              </div>

              {/* Success Filter */}
              <div>
                <label className="admin-filter-label">Status</label>
                <select
                  value={filters.success}
                  onChange={(e) => handleFilterChange('success', e.target.value)}
                  className="admin-filter-control"
                >
                  <option value="">All</option>
                  <option value="true">Success</option>
                  <option value="false">Failed</option>
                </select>
              </div>

              {/* Search Filter */}
              <div className="md:col-span-2 lg:col-span-3">
                <label className="admin-filter-label">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search in action descriptions, user names, or emails..."
                  className="admin-filter-control"
                />
              </div>
            </div>
          </div>
        </AdminCard>
      )}

      {/* Error Display */}
      {error && (
        <div className="admin-card border-red-200 bg-red-50/40">
          <div className="admin-card-body">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <AuditLogsManagementTable
        logs={logs}
        loading={loading}
        onSort={(key, direction) => {
          // Handle sorting if needed
        }}
        onFilter={(filterData) => {
          // Handle DataTable internal filters - merge with existing filters
          const newFilters = {
            ...filters,
            ...filterData
          };
          setFilters(newFilters);
          setCurrentPage(1);
          loadAuditLogs(1, newFilters);
        }}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
      />
    </AdminPageLayout>
  );
};



// Header Actions Component for AdminPanel
export const AuditLogHeaderActions = ({ onToggleFilters, showFilters }) => {
  const { hasPermission } = useAuthStore();
  const [exporting, setExporting] = useState(false);

  const canExportAuditLogs = hasPermission(Permissions.AUDIT_LOG_EXPORT);

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadAuditLogs({});
    } catch (error) {
      console.error('Export failed:', error);
      // You might want to show a toast or error message here
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {canExportAuditLogs && (
        <button
          onClick={handleExport}
          disabled={exporting}
          className="px-2.5 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center text-sm font-medium disabled:opacity-50"
          type="button"
        >
          {exporting ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="-ml-1 mr-2 h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Export CSV
            </>
          )}
        </button>
      )}
      <button
        onClick={onToggleFilters}
        className={`px-2.5 py-1 rounded-md transition-colors flex items-center text-sm font-medium border ${
          showFilters
            ? 'bg-primary-50 text-primary-700 border-primary-200'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
        type="button"
      >
        <svg className="-ml-1 mr-2 h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
        </svg>
        {showFilters ? 'Hide Filters' : 'Show Filters'}
      </button>
    </>
  );
};

export default AuditLogPanel;
